import { FC } from 'react'

import { TypedDocumentNode } from '@apollo/client'
import {
  ProcurementPurchaseRequestFilter,
  PurchaseRequestsOverdueWidgetDocument,
  PurchaseRequestsOverdueWidgetQuery,
  PurchaseRequestsPendingWidgetDocument,
  PurchaseRequestsPendingWidgetQuery,
  PurchaseRequestsTotalWidgetDocument,
  PurchaseRequestsTotalWidgetQuery,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import {
  ChartData,
  FinanceWidget,
  SupportedCharts,
} from 'modules/finance/components/Widgets/common/FinanceWidget'
import { removeNullBetween } from 'modules/finance/components/Widgets/common/utils'
import { buildWidget } from 'modules/finance/components/Widgets/common/widgets'
import { PurchaseRequestsInsightsProps } from 'modules/procurement/components/purchaseRequests/PurchaseRequestsInsights/types'

type WidgetConfig<TQuery> = {
  id: string
  document: TypedDocumentNode<TQuery, PurchaseRequestsInsightsProps>
  prepareData: (data: TQuery) => { groupedChartData: ChartData[] }
}

const getLabelByStatus = (status: string) => {
  switch (status) {
    case 'overdue':
      return i18n.t('finance.overview.purchase_requests.overdue')
    case 'pending':
      return i18n.t('procurement.purchase_requests.status.pending')
    case 'total':
      return i18n.t('finance.overview.purchase_requests.total_requests')
    default:
      return status
  }
}

const createPrepareData =
  (status: string) =>
  (
    data:
      | PurchaseRequestsOverdueWidgetQuery
      | PurchaseRequestsPendingWidgetQuery
      | PurchaseRequestsTotalWidgetQuery
  ) => {
    const count = data?.procurement?.purchaseRequestsConnection?.aggregate?.count || 0

    const groupedChartData: ChartData[] = [
      {
        status,
        label: getLabelByStatus(status),
        value: count,
        formattedValue: i18n.formatNumber(count),
      },
    ]

    return { groupedChartData }
  }

const createWidgetBuilder = <TQuery,>({ id, document, prepareData }: WidgetConfig<TQuery>) => {
  return ({
    queryArgs,
    filters,
  }: {
    queryArgs: PurchaseRequestsInsightsProps
    filters: ProcurementPurchaseRequestFilter
  }) => {
    const WidgetComponent: FC<{ data: ReturnType<typeof prepareData> }> = ({ data }) => {
      const { groupedChartData } = data

      return (
        <FinanceWidget
          id={id}
          type={SupportedCharts.COUNTERS}
          showChart={false}
          countersChartDatas={groupedChartData}
          chartDatas={groupedChartData}
        />
      )
    }

    const filteredPurchaseRequestsFilters = removeNullBetween(filters)

    return buildWidget<TQuery, { groupedChartData: ChartData[] }, PurchaseRequestsInsightsProps>({
      id,
      component: WidgetComponent,
      document,
      prepareData,
      queryArgs: {
        ...queryArgs,
        purchaseRequestFilters: { ...filteredPurchaseRequestsFilters },
      },
    })
  }
}

export const buildPurchaseRequestsOverdueCounterInsightWidget = createWidgetBuilder({
  id: 'purchaseRequestsOverdueWidget',
  document: PurchaseRequestsOverdueWidgetDocument,
  prepareData: createPrepareData('overdue'),
})

export const buildPurchaseRequestsPendingCounterInsightWidget = createWidgetBuilder({
  id: 'purchaseRequestsPendingWidget',
  document: PurchaseRequestsPendingWidgetDocument,
  prepareData: createPrepareData('pending'),
})

export const buildPurchaseRequestsTotalCounterInsightWidget = createWidgetBuilder({
  id: 'purchaseRequestsTotalWidget',
  document: PurchaseRequestsTotalWidgetDocument,
  prepareData: createPrepareData('total'),
})
